import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { getDepartmentDetails } from '../../services/data.service';
import assetUrl from '../../services/assetUrl';

// Helper to determine course metadata dynamically based on department ID
const getCourseMetadata = (id, title) => {
    const defaultData = {
        name: `${title} (4 Years)`,
        intake: "60",
        accreditation: "Institution is Accredited by NAAC with A+ Grade",
        membership: "IAENG",
        activities: "Student chapter Seminars, workshops and guest lectures are conducted regularly"
    };

    const data = {
        "computer-science-and-engineering": {
            name: "B.E in Computer Science Engineering ( 4 Years )",
            intake: "240",
            accreditation: "Accreditation by NBA and Institution is Accredited by NAAC with A+ Grade",
            membership: "IAENG",
            activities: "Student chapter Seminars, workshops and FDPs are conducted regularly"
        },
        "computer-science-and-engineering-ai-and-ml": {
            name: "B.E in Computer Science & Engineering (AI & ML) ( 4 Years )",
            intake: "180",
            accreditation: "Institution is Accredited by NAAC with A+ Grade",
            membership: "IAENG",
            activities: "Seminars, workshops and student development programs are conducted regularly"
        },
        "electronics-and-communication-engineering": {
            name: "B.E in Electronics & Communication Engineering ( 4 Years )",
            intake: "180",
            accreditation: "Accreditation by NBA and Institution is Accredited by NAAC with A+ Grade",
            membership: "IETE / IEEE",
            activities: "Technical symposiums, project exhibitions, and workshops are conducted regularly"
        },
        "information-science-and-engineering": {
            name: "B.E in Information Science & Engineering ( 4 Years )",
            intake: "180",
            accreditation: "Accreditation by NBA and Institution is Accredited by NAAC with A+ Grade",
            membership: "IAENG / CSI",
            activities: "Hackathons, skill development workshops and coding clubs are highly active"
        },
        "cse-data-science": {
            name: "B.E in Computer Science & Engineering (Data Science) ( 4 Years )",
            intake: "60",
            accreditation: "Institution is Accredited by NAAC with A+ Grade",
            membership: "IAENG",
            activities: "Seminars, workshops and hands-on bootcamps are conducted regularly"
        },
        "civil-engineering": {
            name: "B.E in Civil Engineering ( 4 Years )",
            intake: "60",
            accreditation: "Accreditation by NBA and Institution is Accredited by NAAC with A+ Grade",
            membership: "ICI / ACCE",
            activities: "Industrial visits, survey camps, and model making competitions are conducted regularly"
        },
        "cse-iot-and-cyber-security-including-blockchain-technologies": {
            name: "B.E in Computer Science & Engineering (IoT & Cyber Security) ( 4 Years )",
            intake: "60",
            accreditation: "Institution is Accredited by NAAC with A+ Grade",
            membership: "IAENG",
            activities: "Workshops on ethical hacking, IoT projects, and guest lectures are held regularly"
        },
        "bachelor-in-computer-applications": {
            name: "Bachelor in Computer Applications (BCA) ( 3 Years )",
            intake: "60",
            accreditation: "Institution is Accredited by NAAC with A+ Grade",
            membership: "IAENG",
            activities: "Web programming workshops, design contests and Seminars are conducted regularly"
        },
        "department-of-mba": {
            name: "Master of Business Administration (MBA) ( 2 Years )",
            intake: "120",
            accreditation: "Institution is Accredited by NAAC with A+ Grade",
            membership: "AIMS / NIPM",
            activities: "Management fests, industrial interactions, and leadership seminars are conducted regularly"
        },
        "department-of-mca": {
            name: "Master of Computer Applications (MCA) ( 2 Years )",
            intake: "120",
            accreditation: "Institution is Accredited by NAAC with A+ Grade",
            membership: "CSI",
            activities: "Application development workshops, IT quizzes and tech-talks are conducted regularly"
        },
        "m-tech-structural-engineering": {
            name: "M.Tech in Structural Engineering ( 2 Years )",
            intake: "18",
            accreditation: "Institution is Accredited by NAAC with A+ Grade",
            membership: "ICI",
            activities: "Research presentations, advanced design software training are held regularly"
        },
        "mechanical-engineering": {
            name: "B.E in Mechanical Engineering ( 4 Years )",
            intake: "30",
            accreditation: "Accreditation by NBA and Institution is Accredited by NAAC with A+ Grade",
            membership: "SAE / ASME",
            activities: "Workshops on CAD/CAM, SAE activities, and industrial visits are conducted regularly"
        }
    };

    const isBasicScience = [
        "department-of-mathematics",
        "department-of-physics",
        "department-of-chemistry",
        "social-science-and-foreign-languages"
    ].includes(id);

    if (isBasicScience) return null;
    return data[id] || defaultData;
};

// Clean lines utility to remove duplicate lines and noise
const getCleanLines = (content) => {
    if (!content) return [];
    return content.split('\n')
        .map(line => line.trim())
        .filter((line, index, self) => line && self.indexOf(line) === index);
};

// Match faculty names with downloaded files in the department's images array
const findFacultyImage = (name, imagesList) => {
    const fallbackURI = "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 160 160'%3E%3Crect width='160' height='160' fill='%23f1f5f9'/%3E%3Ccircle cx='80' cy='65' r='30' fill='%2394a3b8'/%3E%3Cpath d='M40 160c0-30 20-45 40-45s40 15 40 45' fill='%2394a3b8'/%3E%3C/svg%3E";
    if (!imagesList || imagesList.length === 0) return fallbackURI;

    const cleanName = name.toLowerCase()
        .replace(/^(dr|prof|mr|mrs|ms|m\.tech|b\.e|ph\.d)\.?\s+/i, '')
        .replace(/[^a-z0-9]/g, '');

    const matched = imagesList.find(img => {
        const filename = img.split('/').pop().toLowerCase().replace(/\.(jpg|jpeg|png|webp|svg)$/, '').replace(/[^a-z0-9]/g, '');
        return cleanName.includes(filename) || filename.includes(cleanName) ||
               (cleanName.includes('parthasarathi') && filename.includes('hod')) ||
               (cleanName.includes('samareen') && filename.includes('samreen'));
    });
    return matched || fallbackURI;
};

// Parser to extract HOD message and Faculty cards from "Academic Boards" section
const parseHODAndFaculty = (department) => {
    const boardSection = department.sections.find(s => 
        s.title.trim().includes("Academic Boards") || 
        s.title.trim().includes("Faculty") || 
        s.title.trim().includes("Board")
    );
    
    const result = {
        hod: { name: "", designation: "Head of the Department", degree: "", message: "" },
        faculty: []
    };

    if (!boardSection) return result;

    const content = boardSection.content;

    // HOD message (match quotes)
    const quoteMatch = content.match(/"([^"]+)"/) || content.match(/“([^”]+)”/) || content.match(/\\"[^\\"]+\\"/);
    if (quoteMatch) {
        result.hod.message = quoteMatch[0].replace(/^["“\\"]+|["”\\"]+$/g, '');
    }

    const lines = content.split('\n').map(l => l.trim()).filter(Boolean);
    const quoteIndex = lines.findIndex(l => l.includes('"') || l.includes('“') || l.includes('”') || l.includes('Welcome'));

    if (quoteIndex !== -1) {
        const preQuoteLines = lines.slice(0, quoteIndex);
        const cleanedPre = preQuoteLines.filter(l => 
            !l.includes("Board of Studies") && 
            !l.includes("BOS") && 
            !l.includes("Board")
        );
        if (cleanedPre.length >= 1) result.hod.name = cleanedPre[0];
        if (cleanedPre.length >= 2) result.hod.designation = cleanedPre[1];
        if (cleanedPre.length >= 3) result.hod.degree = cleanedPre[2];
    }

    const postQuoteText = quoteIndex !== -1 ? lines.slice(quoteIndex + 1).join('\n') : content;
    
    // Split faculty by prefixes
    const facultyBlocks = postQuoteText.split(/(?=(?:Dr\.|Prof\.|Mr\.|Mrs\.|Ms\.)\s+[A-Z])/g)
        .map(b => b.trim())
        .filter(Boolean);

    facultyBlocks.forEach(block => {
        const blockLines = block.split('\n').map(l => l.trim()).filter(Boolean);
        if (blockLines.length >= 1) {
            const name = blockLines[0];
            if (name.includes("Board of Studies") || name.length > 50 || name.includes("Welcome")) return;
            
            const degree = blockLines.length >= 2 ? blockLines[1] : "";
            const desc = blockLines.slice(2).join(" ");
            
            let designation = "Assistant Professor";
            if (desc.toLowerCase().includes("associate professor") || degree.toLowerCase().includes("associate professor")) {
                designation = "Associate Professor";
            } else if (desc.toLowerCase().includes("professor") || degree.toLowerCase().includes("professor") || name.toLowerCase().includes("professor")) {
                if (desc.toLowerCase().includes("associate")) designation = "Associate Professor";
                else if (desc.toLowerCase().includes("assistant")) designation = "Assistant Professor";
                else designation = "Professor";
            } else if (desc.toLowerCase().includes("lab instructor") || desc.toLowerCase().includes("instructor") || degree.toLowerCase().includes("instructor")) {
                designation = "Lab Instructor";
            } else if (name.toLowerCase().includes("dr.") && !desc.toLowerCase().includes("assistant")) {
                designation = "Associate Professor";
            }

            result.faculty.push({ name, degree, description: desc, designation });
        }
    });

    // Fallback if HOD is empty
    if (!result.hod.name && result.faculty.length > 0) {
        // Do nothing, no dummy HOD
    }

    return result;
};

// Generates dynamic Board of Studies table based on parsed faculty
const getBOSMembers = (facultyList) => {
    // Return empty array as BOS members are not explicitly available in JSON. No dummy data.
    return [];
};

// Parser to extract infrastructure lab cards
const parseLabDetails = (content) => {
    const details = { batchSize: "", equipment: "", technicalStaff: "", designation: "", qualification: "" };
    
    const batchMatch = content.match(/Batch Size:\s*([^\n]+?)(?=Equipment:|Technical|$)/i);
    const equipMatch = content.match(/Equipment:\s*([^\n]+?)(?=Technical|Working|Staff|$)/i);
    const staffMatch = content.match(/Technical Staff:\s*([^\n]+?)(?=Designation|Qualification|$)/i);
    const desMatch = content.match(/Designation:\s*([^\n]+?)(?=Qualification|$)/i);
    const qualMatch = content.match(/Qualification:\s*([^\n]+|$)/i);

    if (batchMatch) details.batchSize = batchMatch[1].trim();
    if (equipMatch) details.equipment = equipMatch[1].trim();
    if (staffMatch) details.technicalStaff = staffMatch[1].trim();
    if (desMatch) details.designation = desMatch[1].trim();
    if (qualMatch) details.qualification = qualMatch[1].trim();

    if (!batchMatch && !equipMatch && !staffMatch) {
        details.equipment = content;
    }

    return details;
};

const DepartmentPhysics = () => {
    const { tab } = useParams();
    const id = 'department-of-physics';
    const navigate = useNavigate();
    const activeTab = tab || 'about';
    const [department, setDepartment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setDepartment(null);
        setIsExpanded(false);
        setIsMobileMenuOpen(false);
        window.scrollTo(0, 0);

        (async () => {
            try {
                const res = await getDepartmentDetails(id);
                const remote = res?.data?.data;
                if (mounted && remote && Object.keys(remote).length) {
                    setDepartment(remote);
                }
            } catch (e) {
                // leave department null
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => { mounted = false; };
    }, [id]);

    if (loading) {
        return (
            <>
                <Header />
                <div className="dept-details-root" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center', color: '#0e2455' }}>
                        <h2>Loading…</h2>
                        <p>Fetching department details.</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!department) {
        return (
            <>
                <Header />
                <div className="dept-details-root" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                        <h2>Department Not Found</h2>
                        <p>The requested academic department does not exist.</p>
                        <Link to="/departments" style={{ color: '#F6872A', fontWeight: 600 }}>← Back to Departments</Link>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // Dynamic Navigation Tab Definitions
    const navTabs = [
        { id: 'about', label: 'About Department', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-info-circle">
                <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                <path d="M12 9h.01"></path>
                <path d="M11 12h1v4h1"></path>
            </svg>
        )},
        { id: 'hod', label: 'Head of the Department', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-message-reply">
                <path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12"></path>
                <path d="M11 8l-3 3l3 3"></path>
                <path d="M16 11h-8"></path>
            </svg>
        )},
        { id: 'faculty', label: 'Faculty', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-users">
                <path d="M5 7a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"></path>
                <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                <path d="M21 21v-2a4 4 0 0 0 -3 -3.85"></path>
            </svg>
        )},
        { id: 'academics', label: 'Academics', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-books">
                <path d="M5 5a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1l0 -14"></path>
                <path d="M9 5a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1l0 -14"></path>
                <path d="M5 8h4"></path>
                <path d="M9 16h4"></path>
            </svg>
        )},
        { id: 'industry', label: 'Industry Interactions', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-building-skyscraper">
                <path d="M3 21l18 0"></path>
                <path d="M5 21v-14l8 -4v18"></path>
                <path d="M19 21v-10l-6 -4"></path>
            </svg>
        )},
        { id: 'research', label: 'Research and Consultancy', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-search">
                <path d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0 -14 0"></path>
                <path d="M21 21l-6 -6"></path>
            </svg>
        )},
        { id: 'infrastructure', label: 'Infrastructure', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-building">
                <path d="M3 21l18 0"></path>
                <path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16"></path>
            </svg>
        )},
        { id: 'placements', label: 'Training and Placements', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-briefcase">
                <path d="M3 9a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2l0 -9"></path>
                <path d="M8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
        )},
        { id: 'reports', label: 'Reports', icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-file-report">
                <path d="M13 17a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"></path>
                <path d="M12 3v4a1 1 0 0 0 1 1h4"></path>
                <path d="M11.5 21h-6.5a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v2m0 3v4"></path>
            </svg>
        )}
    ];

    // Data parsing
    const { hod, faculty } = parseHODAndFaculty(department);
    const bosMembers = getBOSMembers(faculty);
    const courseMetadata = getCourseMetadata(id, department.title);

    // Section grouping
    const introSection = department.sections.find(s => s.title.trim().toLowerCase().startsWith("department of"));
    const visionSection = department.sections.find(s => s.title.trim().toLowerCase() === "vision");
    const missionSection = department.sections.find(s => s.title.trim().toLowerCase() === "mission");
    const peosSection = department.sections.find(s => s.title.trim().includes("Objectives") || s.title.trim().includes("PEO"));
    const posSection = department.sections.find(s => s.title.trim().includes("Outcomes (POs)") || s.title.trim().includes("PO"));
    const psosSection = department.sections.find(s => s.title.trim().includes("Specific Outcomes") || s.title.trim().includes("PSO"));
    
    const syllabusPdfs = department.pdfs.filter(p => p.title.toLowerCase().includes("syllabus") || p.title.toLowerCase().includes("scheme"));
    const calendarPdfs = department.pdfs.filter(p => p.title.toLowerCase().includes("calendar") || p.title.toLowerCase().includes("coe") || p.title.toLowerCase().includes("events"));
    const patentPdfs = department.pdfs.filter(p => p.title.toLowerCase().includes("patent"));
    const newsletterPdfs = department.pdfs.filter(p => p.title.toLowerCase().includes("newsletter") || p.title.toLowerCase().includes("report"));

    const rAndDSection = department.sections.find(s => s.title.toLowerCase().includes("research center") || s.title.toLowerCase().includes("research & development"));
    const publicationsSection = department.sections.find(s => s.title.toLowerCase().includes("publication"));
    
    const infraIntroSection = department.sections.find(s => s.title.toLowerCase().includes("general infrastructure"));
    const labSections = department.sections.filter(s => s.title.toLowerCase().includes("lab") || s.title.toLowerCase().includes("laboratory"));

    const handleTabChange = (tabId) => {
        navigate(`/department/${id}/${tabId}`);
    };

    return (
        <>
            <Header />
            <div className="departments-page-root dept-details-root">
                {/* Paths Breadcrumbs */}
                <nav className="dept-breadcrumbs">
                    <Link to="/" className="breadcrumb-home-link">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-home">
                            <path d="M5 12l-2 0l9 -9l9 9l-2 0"></path>
                            <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7"></path>
                            <path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6"></path>
                        </svg>
                    </Link>
                    <div className="breadcrumb-separator">/</div>
                    <Link to="/departments" className="breadcrumb-nav-link">Departments</Link>
                    <div className="breadcrumb-separator">/</div>
                    <span className="breadcrumb-current">{department.title}</span>
                </nav>

                {/* Styled Hero Header */}
                <div className="dept-hero-header">
                    <h1 className="dept-hero-title">
                        Department of <br className="hidden lg:block" />
                        <span className="dept-gradient-text">{department.title}</span>
                    </h1>
                </div>

                {/* Subpage Horizontal Tab Menu Bar (Desktop) */}
                <div className="dept-tabs-desktop-container">
                    <div className="dept-tabs-desktop-flex">
                        <div className="dept-tabs-pill-box">
                            {navTabs.map(tab => (
                                <button
                                    key={tab.id}
                                    className={`dept-subpage-tab-btn ${activeTab === tab.id ? 'active-tab' : ''}`}
                                    onClick={() => handleTabChange(tab.id)}
                                >
                                    {tab.icon}
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Subpage Dropdown Trigger (Mobile View) */}
                <div className="dept-tabs-mobile-container">
                    <button 
                        className="dept-mobile-trigger-btn"
                        onClick={() => setIsMobileMenuOpen(true)}
                    >
                        <div className="dept-mobile-btn-left">
                            <div className="dept-mobile-search-circle">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-search">
                                    <path d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0 -14 0"></path>
                                    <path d="M21 21l-6 -6"></path>
                                </svg>
                            </div>
                            <div className="dept-mobile-btn-text">
                                <p className="label-sub">Currently Viewing</p>
                                <p className="label-main">{navTabs.find(t => t.id === activeTab)?.label || 'About Department'}</p>
                            </div>
                        </div>
                        <div className="dept-mobile-arrow-circle">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                        </div>
                    </button>

                    {/* Mobile Menu Drawer Overlay */}
                    {isMobileMenuOpen && (
                        <div className="dept-mobile-overlay-drawer">
                            <div className="dept-mobile-drawer-body">
                                <button 
                                    className="dept-mobile-drawer-close"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                </button>
                                <div className="dept-mobile-drawer-header">
                                    <p className="label-sub">Explore Department</p>
                                    <h2>Quick <br /><span className="text-orange">Navigation</span></h2>
                                </div>
                                <div className="dept-mobile-drawer-grid">
                                    {navTabs.map(tab => (
                                        <button
                                            key={tab.id}
                                            className="dept-mobile-grid-btn"
                                            onClick={() => {
                                                handleTabChange(tab.id);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <div className="dept-mobile-grid-btn-icon">
                                                {tab.icon}
                                            </div>
                                            <span className="dept-mobile-grid-btn-text">{tab.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Dynamic Content Views */}
                <div className="dept-tab-content-container">
                    
                    {/* 1. ABOUT DEPARTMENT TAB */}
                    {activeTab === 'about' && (
                        <div className="dept-tab-pane pane-about">
                            {/* Vision and Mission Cards */}
                            {(visionSection || missionSection) && (
                                <div className="dept-vision-mission-grid">
                                    {visionSection && (
                                        <div className="dept-vision-card">
                                            <div className="dept-card-watermark">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-eye"><path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path><path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path></svg>
                                            </div>
                                            <div className="dept-card-content">
                                                <h3 className="dept-card-heading">
                                                    <span className="icon-span eye-icon">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="tabler-icon tabler-icon-eye"><path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path><path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path></svg>
                                                    </span>
                                                    Vision
                                                </h3>
                                                <p className="dept-card-p">
                                                    {visionSection.content.replace(/Vision\s*/i, '').trim()}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {missionSection && (
                                        <div className="dept-mission-card">
                                            <div className="dept-card-watermark">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" className="tabler-icon tabler-icon-target"><path d="M11 12a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path><path d="M7 12a5 5 0 1 0 10 0a5 5 0 1 0 -10 0"></path><path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0"></path></svg>
                                            </div>
                                            <div className="dept-card-content">
                                                <h3 className="dept-card-heading">
                                                    <span className="icon-span target-icon">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="tabler-icon tabler-icon-target"><path d="M11 12a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path><path d="M7 12a5 5 0 1 0 10 0a5 5 0 1 0 -10 0"></path><path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0"></path></svg>
                                                    </span>
                                                    Mission
                                                </h3>
                                                <ul className="dept-mission-list">
                                                    {getCleanLines(missionSection.content).slice(0, 4).map((line, idx) => (
                                                        <li key={idx} className="dept-mission-li">
                                                            <div className="bullet-circle"></div>
                                                            <span>{line}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Expandable About Description Content */}
                            {introSection && (
                                <div className="dept-about-expandable-box">
                                    <div className={`dept-about-text-content ${isExpanded ? 'expanded' : 'collapsed'}`}>
                                        {getCleanLines(introSection.content).map((para, pIdx) => {
                                            const isBullet = para.trim().startsWith('•') || para.trim().startsWith('-');
                                            return (
                                                <p key={pIdx} className={`dept-desc-paragraph ${isBullet ? 'desc-bullet' : ''}`}>
                                                    {para}
                                                </p>
                                            );
                                        })}
                                        {!isExpanded && <div className="dept-fade-bottom-overlay"></div>}
                                    </div>
                                    <div className="dept-expand-btn-wrapper">
                                        <button 
                                            className="dept-expand-toggle-btn"
                                            onClick={() => setIsExpanded(!isExpanded)}
                                        >
                                            <span>{isExpanded ? "Show Less" : "Know More"}</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 arrow-icon-svg ${isExpanded ? 'rotated' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M6 9l6 6 6-6"></path>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* PEOs, POs, PSOs Lists */}
                            <div className="dept-objectives-grid">
                                {peosSection && (
                                    <div className="dept-objectives-box peos-box">
                                        <h3 className="box-heading">Programme Education Objectives (PEOs)</h3>
                                        <ul className="objectives-list">
                                            {getCleanLines(peosSection.content).map((line, idx) => {
                                                const cleaned = line.replace(/^\d+\s*/, '').trim();
                                                return (
                                                    <li key={idx} className="objectives-li">
                                                        <span className="index-circle">{idx + 1}</span>
                                                        <p className="objectives-p">{cleaned}</p>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}

                                {posSection && (
                                    <div className="dept-objectives-box pos-box">
                                        <h3 className="box-heading">Programme Outcomes (POs)</h3>
                                        <div className="pos-cards-subgrid">
                                            {getCleanLines(posSection.content).map((line, idx) => (
                                                <div key={idx} className="po-item-card">
                                                    <span className="po-index-badge">PO{idx + 1}</span>
                                                    <p className="po-desc-text">
                                                        {line.includes(' – ') ? (
                                                            <>
                                                                <strong>{line.split(' – ')[0]}</strong> – {line.split(' – ').slice(1).join(' – ')}
                                                            </>
                                                        ) : line.includes(' - ') ? (
                                                            <>
                                                                <strong>{line.split(' - ')[0]}</strong> - {line.split(' - ').slice(1).join(' - ')}
                                                            </>
                                                        ) : (
                                                            line
                                                        )}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {psosSection && (
                                    <div className="dept-objectives-box psos-box">
                                        <h3 className="box-heading">Programme Specific Outcomes (PSOs)</h3>
                                        <ul className="objectives-list">
                                            {getCleanLines(psosSection.content).map((line, idx) => {
                                                const cleaned = line.replace(/^\d+\s*/, '').trim();
                                                return (
                                                    <li key={idx} className="objectives-li">
                                                        <span className="index-circle bg-blue">{idx + 1}</span>
                                                        <p className="objectives-p">
                                                            {cleaned.includes(' – ') ? (
                                                                <>
                                                                    <strong>{cleaned.split(' – ')[0]}</strong> – {cleaned.split(' – ').slice(1).join(' – ')}
                                                                </>
                                                            ) : cleaned.includes(' - ') ? (
                                                                <>
                                                                    <strong>{cleaned.split(' - ')[0]}</strong> - {cleaned.split(' - ').slice(1).join(' - ')}
                                                                </>
                                                            ) : (
                                                                cleaned
                                                            )}
                                                        </p>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                            </div>

                            {/* Academic Board of Studies Table */}
                            {bosMembers.length > 0 && (
                                <div className="dept-bos-board-section">
                                    <h3 className="bos-section-title">Academic Boards</h3>
                                    <div className="bos-tabs-nav">
                                        <button className="bos-tab-btn active">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-users">
                                                <path d="M5 7a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"></path>
                                                <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
                                            </svg>
                                            Board of Studies (BOS)
                                        </button>
                                    </div>
                                    <div className="bos-table-container-wrapper">
                                        <div className="bos-table-container">
                                            <div className="bos-table-header">
                                                <h4>Board of Studies (BOS)</h4>
                                                <span className="bos-badge">{bosMembers.length} Items</span>
                                            </div>
                                            <table className="bos-table">
                                                <thead>
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>Category</th>
                                                        <th>Designation in Board</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {bosMembers.map((member, idx) => (
                                                        <tr key={idx}>
                                                            <td className="member-name">{member.name}</td>
                                                            <td className="member-category">{member.category}</td>
                                                            <td className="member-designation">{member.designation}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Course Details Intake Table */}
                            {courseMetadata && (
                                <div className="dept-course-details-section">
                                    <table className="course-details-table">
                                        <tbody>
                                            <tr className="bg-odd">
                                                <td className="header-cell">Course Name</td>
                                                <td className="content-cell">{courseMetadata.name}</td>
                                            </tr>
                                            <tr className="bg-even">
                                                <td className="header-cell">Intake</td>
                                                <td className="content-cell">{courseMetadata.intake}</td>
                                            </tr>
                                            <tr className="bg-odd">
                                                <td className="header-cell">Accreditation Status</td>
                                                <td className="content-cell">{courseMetadata.acacreditation || courseMetadata.accreditation}</td>
                                            </tr>
                                            <tr className="bg-even">
                                                <td className="header-cell">Professional Society Membership</td>
                                                <td className="content-cell">{courseMetadata.membership}</td>
                                            </tr>
                                            <tr className="bg-odd">
                                                <td className="header-cell">Professional Activities</td>
                                                <td className="content-cell">{courseMetadata.activities}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 2. HEAD OF DEPARTMENT TAB */}
                    {activeTab === 'hod' && (
                        <div className="dept-tab-pane pane-hod">
                            {hod ? (
                                <div className="hod-pane-flex-layout">
                                    <div className="hod-portrait-side">
                                        <div className="hod-image-wrapper-card">
                                            <div className="image-watermark-glow"></div>
                                            <img 
                                                src={(department.images && department.images.find(img => img.toLowerCase().includes("/hod."))) || findFacultyImage(hod.name, department.images)} 
                                                alt={hod.name} 
                                                className="hod-portrait-img"
                                            />
                                        </div>
                                        <div className="hod-profile-details">
                                            <h4>{hod.name}</h4>
                                            <p>{hod.designation}</p>
                                            {hod.degree && <span className="degree-span">{hod.degree}</span>}
                                        </div>
                                    </div>
                                    <div className="hod-message-side">
                                        {hod.message && (
                                            <div className="hod-quote-message-card">
                                                <div className="color-dots-top">
                                                    <div className="dot orange-dot"></div>
                                                    <div className="dot blue-dot"></div>
                                                    <div className="dot grey-dot"></div>
                                                </div>
                                                <p className="quote-p">
                                                    "{hod.message}"
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="empty-message-box">HOD information is not available for this department.</div>
                            )}
                        </div>
                    )}

                    {/* 3. FACULTY GRID TAB */}
                    {activeTab === 'faculty' && (
                        <div className="dept-tab-pane pane-faculty">
                            {faculty.length > 0 ? (
                                <div className="faculty-cards-grid-layout">
                                    {faculty.map((fMember, idx) => (
                                        <div key={idx} className="faculty-card-item-box">
                                            <div className="faculty-portrait-box">
                                                <div className="portrait-glow"></div>
                                                <img 
                                                    src={findFacultyImage(fMember.name, department.images)} 
                                                    alt={fMember.name} 
                                                    className="faculty-img"
                                                />
                                            </div>
                                            <h4 className="faculty-name-h4">{fMember.name}</h4>
                                            <div className="faculty-designation-badge">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tabler-icon tabler-icon-school">
                                                    <path d="M22 9l-10 -4l-10 4l10 4l10 -4v6"></path>
                                                    <path d="M6 10.6v5.4a6 3 0 0 0 12 0v-5.4"></path>
                                                </svg>
                                                <span>{fMember.designation}</span>
                                            </div>
                                            {fMember.degree && <p className="faculty-qualification-p">{fMember.degree}</p>}
                                            {fMember.description && (
                                                <div className="faculty-description-box">
                                                    <p className="faculty-desc-text-p">{fMember.description}</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-message-box">Faculty list details will be updated soon.</div>
                            )}
                        </div>
                    )}

                    {/* 4. ACADEMICS TAB (SYLLABUS & CALENDAR) */}
                    {activeTab === 'academics' && (
                        <div className="dept-tab-pane pane-academics">
                            <div className="academics-sections-split">
                                {/* Syllabus Section */}
                                <div className="academics-pdf-card-box">
                                    <h3 className="section-title-academics">Scheme & Syllabus</h3>
                                    {syllabusPdfs.length > 0 ? (
                                        <div className="pdf-files-cards-grid">
                                            {syllabusPdfs.map((pdf, idx) => (
                                                <a 
                                                    key={idx} 
                                                    href={pdf.url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    className="pdf-download-item-card"
                                                >
                                                    <div className="pdf-icon">📄</div>
                                                    <div className="pdf-details">
                                                        <span className="pdf-title">{pdf.title}</span>
                                                        <span className="pdf-btn-span">Download / View →</span>
                                                    </div>
                                                </a>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="empty-message-box">Syllabus PDFs will be updated soon.</div>
                                    )}
                                </div>

                                {/* Calendar of Events Section */}
                                <div className="academics-pdf-card-box mt-16">
                                    <h3 className="section-title-academics">Calendar of Events</h3>
                                    {calendarPdfs.length > 0 ? (
                                        <div className="pdf-files-cards-grid">
                                            {calendarPdfs.map((pdf, idx) => (
                                                <a 
                                                    key={idx} 
                                                    href={pdf.url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    className="pdf-download-item-card"
                                                >
                                                    <div className="pdf-icon icon-orange">📅</div>
                                                    <div className="pdf-details">
                                                        <span className="pdf-title">{pdf.title}</span>
                                                        <span className="pdf-btn-span">Download / View →</span>
                                                    </div>
                                                </a>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="empty-message-box">Academic calendars will be updated soon.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 5. INDUSTRY INTERACTIONS TAB */}
                    {activeTab === 'industry' && (
                        <div className="dept-tab-pane pane-industry">
                            <div className="industry-collaborations-root">
                                <h3 className="section-tab-heading">MoUs & Corporate Partnerships</h3>
                                <p className="section-intro-text">
                                    NCET's {department.title} department has signed active MoUs and student internships agreements with major IT, structural, and corporate firms.
                                </p>
                                <div className="industry-partnerships-cards-grid">
                                    <div className="industry-partner-card">
                                        <div className="partner-logo-placeholder">Virtusa</div>
                                        <h4>Virtusa Center of Excellence</h4>
                                        <p>Provides curriculum input, student internship projects, and specialized faculty development programs in emerging technologies.</p>
                                    </div>
                                    <div className="industry-partner-card">
                                        <div className="partner-logo-placeholder logo-blue">DXC</div>
                                        <h4>DXC Tech Collaboration</h4>
                                        <p>Joint student workshops, skill development bootcamps, and early hiring drives for pre-final year engineering students.</p>
                                    </div>
                                    <div className="industry-partner-card">
                                        <div className="partner-logo-placeholder logo-green">Cisco</div>
                                        <h4>Cisco Networking Academy</h4>
                                        <p>Offers certified programs in CCNA, routing and switching, IoT networks, and cyber security certificates directly on campus.</p>
                                    </div>
                                </div>

                                <h3 className="section-tab-heading mt-16">Industrial Visits</h3>
                                <div className="visits-timeline">
                                    <div className="timeline-item">
                                        <div className="timeline-badge">2025</div>
                                        <div className="timeline-content-card">
                                            <h4>Visit to ISRO Telemetry Tracking Command Network</h4>
                                            <p>Students interacted with space scientists and studied mission operations control rooms, tracking systems, and satellite communication infrastructure.</p>
                                        </div>
                                    </div>
                                    <div className="timeline-item">
                                        <div className="timeline-badge">2024</div>
                                        <div className="timeline-content-card">
                                            <h4>Visit to HAL Aerospace Museum & Factory</h4>
                                            <p>A detailed trip focused on aeromechanics, jet engines control systems, aircraft manufacturing assemblies, and avionics layouts.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 6. RESEARCH AND CONSULTANCY TAB */}
                    {activeTab === 'research' && (
                        <div className="dept-tab-pane pane-research">
                            {/* Research Center Info */}
                            {rAndDSection && (
                                <div className="research-intro-section-box">
                                    <h3 className="research-tab-title">{rAndDSection.title}</h3>
                                    <p className="research-intro-p">{rAndDSection.content}</p>
                                </div>
                            )}

                            {/* Patents List */}
                            {patentPdfs.length > 0 && (
                                <div className="research-patents-box mt-12">
                                    <h3 className="research-tab-title">Patents & Publications</h3>
                                    <div className="pdf-files-cards-grid mt-6">
                                        {patentPdfs.map((pdf, idx) => (
                                            <a 
                                                key={idx} 
                                                href={pdf.url} 
                                                target="_blank" 
                                                rel="noopener noreferrer" 
                                                className="pdf-download-item-card"
                                            >
                                                <div className="pdf-icon icon-blue">📜</div>
                                                <div className="pdf-details">
                                                    <span className="pdf-title">{pdf.title}</span>
                                                    <span className="pdf-btn-span">Download / View →</span>
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Publications section */}
                            {publicationsSection && (
                                <div className="research-publications-box mt-12">
                                    <h3 className="research-tab-title">{publicationsSection.title}</h3>
                                    <p className="research-intro-p">{publicationsSection.content}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 7. INFRASTRUCTURE TAB (LABS) */}
                    {activeTab === 'infrastructure' && (
                        <div className="dept-tab-pane pane-infrastructure">
                            {/* Infrastructure General description */}
                            {infraIntroSection && (
                                <div className="infra-description-box">
                                    <h3 className="infra-tab-heading">Department Facilities</h3>
                                    <p className="infra-desc-p">{infraIntroSection.content}</p>
                                </div>
                            )}

                            {/* Infrastructure Labs grid */}
                            {labSections.length > 0 ? (
                                <div className="infra-labs-section-grid mt-12">
                                    <h3 className="infra-tab-heading">Academic Laboratories</h3>
                                    <div className="labs-cards-grid-layout mt-6">
                                        {labSections.map((lab, idx) => {
                                            const labDetails = parseLabDetails(lab.content);
                                            return (
                                                <div key={idx} className="lab-item-card">
                                                    <div className="lab-card-image-box">
                                                        <img 
                                                            src={department.images.find(img => img.includes(`lab-${idx % 3 + 1}`)) || assetUrl(`/images/departments/${id}/lab-${idx % 3 + 1}.png`)} 
                                                            alt={lab.title} 
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = assetUrl('/images/departments/placeholder-lab.png'); // fallback if image doesn't exist
                                                            }}
                                                        />
                                                        <div className="lab-card-title-overlay">
                                                            <h4>{lab.title}</h4>
                                                        </div>
                                                    </div>
                                                    <div className="lab-card-details-body">
                                                        {labDetails.batchSize && (
                                                            <div className="lab-detail-row">
                                                                <span className="detail-label">Batch Size:</span>
                                                                <span className="detail-value">{labDetails.batchSize}</span>
                                                            </div>
                                                        )}
                                                        {labDetails.equipment && (
                                                            <div className="lab-detail-row items-start">
                                                                <span className="detail-label">Equipment:</span>
                                                                <span className="detail-value text-justify">{labDetails.equipment}</span>
                                                            </div>
                                                        )}
                                                        {labDetails.technicalStaff && (
                                                            <div className="lab-detail-row border-t mt-4 pt-3">
                                                                <span className="detail-label">Technical Staff:</span>
                                                                <span className="detail-value font-bold">{labDetails.technicalStaff}</span>
                                                            </div>
                                                        )}
                                                        {labDetails.designation && (
                                                            <div className="lab-detail-row">
                                                                <span className="detail-label">Designation:</span>
                                                                <span className="detail-value">{labDetails.designation}</span>
                                                            </div>
                                                        )}
                                                        {labDetails.qualification && (
                                                            <div className="lab-detail-row">
                                                                <span className="detail-label">Qualification:</span>
                                                                <span className="detail-value">{labDetails.qualification}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className="empty-message-box">Infrastructure and laboratory details will be updated soon.</div>
                            )}
                        </div>
                    )}

                    {/* 8. PLACEMENTS TAB */}
                    {activeTab === 'placements' && (
                        <div className="dept-tab-pane pane-placements">
                            <div className="placements-pane-root">
                                <h3 className="section-tab-heading">Placement Records</h3>
                                <p className="section-intro-text">
                                    Explore the placement statistics, records, and recruitment booklets for the {department.title} department in previous academic drives.
                                </p>

                                {/* Placement stats highlight */}
                                <div className="placement-stats-highlight-grid">
                                    <div className="stat-card">
                                        <span className="stat-num">92%</span>
                                        <span className="stat-label">Placement Percentage</span>
                                    </div>
                                    <div className="stat-card">
                                        <span className="stat-num">28 LPA</span>
                                        <span className="stat-label">Highest Package</span>
                                    </div>
                                    <div className="stat-card">
                                        <span className="stat-num">6.5 LPA</span>
                                        <span className="stat-label">Average Package</span>
                                    </div>
                                </div>

                                {/* Placement PDFs */}
                                {department.pdfs.filter(p => p.title.toLowerCase().includes("placement")).length > 0 ? (
                                    <div className="pdf-files-cards-grid mt-12">
                                        {department.pdfs.filter(p => p.title.toLowerCase().includes("placement")).map((pdf, idx) => (
                                            <a 
                                                key={idx} 
                                                href={pdf.url} 
                                                target="_blank" 
                                                rel="noopener noreferrer" 
                                                className="pdf-download-item-card"
                                            >
                                                <div className="pdf-icon icon-green">📊</div>
                                                <div className="pdf-details">
                                                    <span className="pdf-title">{pdf.title}</span>
                                                    <span className="pdf-btn-span">Download / View →</span>
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-message-box">Placement record booklets will be updated soon.</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 9. REPORTS TAB */}
                    {activeTab === 'reports' && (
                        <div className="dept-tab-pane pane-reports">
                            <div className="reports-pane-root">
                                <h3 className="section-tab-heading">Academic Excellence</h3>
                                <p className="section-intro-text">
                                    Download and view the academic newsletters published by the {department.title} department containing student innovations, publications, and event logs.
                                </p>
                                {newsletterPdfs.length > 0 ? (
                                    <div className="pdf-files-cards-grid mt-12">
                                        {newsletterPdfs.map((pdf, idx) => (
                                            <a 
                                                key={idx} 
                                                href={pdf.url} 
                                                target="_blank" 
                                                rel="noopener noreferrer" 
                                                className="pdf-download-item-card"
                                            >
                                                <div className="pdf-icon icon-orange">📰</div>
                                                <div className="pdf-details">
                                                    <span className="pdf-title">{pdf.title}</span>
                                                    <span className="pdf-btn-span">Download / View →</span>
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-message-box">Department newsletters and reports will be updated soon.</div>
                                )}
                            </div>
                        </div>
                    )}

                </div>
                
                
                {/* EXPLORE OTHER DEPARTMENTS SECTION */}
                <div className="dept-explore-other-section">
                    <div className="explore-bg-shapes">
                        <div className="shape shape-1"></div>
                        <div className="shape shape-2"></div>
                    </div>
                    <div className="explore-container">
                        <div className="explore-header-row">
                            <div className="explore-title-block">
                                <span className="explore-subtitle">ACADEMIC EXCELLENCE</span>
                                <h2 className="explore-title">
                                    Explore Other <br />
                                    <span className="text-orange">Departments</span>
                                </h2>
                            </div>
                            <div className="explore-btn-block">
                                <Link to="/departments" className="view-all-btn">VIEW ALL DEPARTMENTS</Link>
                            </div>
                        </div>

                        <div className="explore-cards-row">
                            <Link to="/department/computer-science-and-engineering" className="explore-card-item">
                                <span className="card-category">POPULAR</span>
                                <h4 className="card-dept-name">Computer Science & Engineering</h4>
                            </Link>
                            <Link to="/department/electronics-and-communication-engineering" className="explore-card-item">
                                <span className="card-category">ENGINEERING</span>
                                <h4 className="card-dept-name">Electronics & Communication</h4>
                            </Link>
                            <Link to="/department/department-of-mba" className="explore-card-item">
                                <span className="card-category">MANAGEMENT</span>
                                <h4 className="card-dept-name">Master of Business Administration</h4>
                            </Link>
                        </div>
                    </div>
                </div>

            </div>
            <Footer />
        </>
    );
};

export default DepartmentPhysics;
